/**
 * File System Access mount management for the web build.
 *
 * The fs-op engine lives in shims/fsa-core.cjs (shared with the fs shims);
 * this module owns the user-facing side: the directory picker, persistence
 * of directory handles in IndexedDB so mounts survive reloads, and the
 * permission re-grant flow (handles restore in a "locked" state until the
 * user's first gesture lets us call requestPermission()).
 */
import core from "../shims/fsa-core.cjs";
const DB_NAME = "vortex-web-fsa";
const DB_VERSION = 1;
const STORE_MOUNTS = "mounts";
function openDb() {
    return new Promise((resolve, reject) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_MOUNTS)) {
                db.createObjectStore(STORE_MOUNTS, { keyPath: "mountPath" });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}
async function idbGetAll() {
    const db = await openDb();
    try {
        return await new Promise((resolve, reject) => {
            const request = db
                .transaction(STORE_MOUNTS, "readonly")
                .objectStore(STORE_MOUNTS)
                .getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
    finally {
        db.close();
    }
}
async function idbPut(record) {
    const db = await openDb();
    try {
        await new Promise((resolve, reject) => {
            const request = db
                .transaction(STORE_MOUNTS, "readwrite")
                .objectStore(STORE_MOUNTS)
                .put(record);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
    finally {
        db.close();
    }
}
function slugify(name) {
    const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug.length > 0 ? slug : "folder";
}
function uniqueMountPath(label) {
    const taken = new Set(core.listMounts().map((mount) => mount.mountPath));
    const base = `/fsa/${slugify(label)}`;
    if (!taken.has(base)) {
        return base;
    }
    let counter = 2;
    while (taken.has(`${base}-${counter}`)) {
        counter++;
    }
    return `${base}-${counter}`;
}
async function queryPermission(handle) {
    if (typeof handle.queryPermission !== "function") {
        return "granted"; // test fakes without permission plumbing
    }
    return handle.queryPermission({ mode: "readwrite" });
}
// ---------------------------------------------------------------------------
// permission re-grant: requestPermission() needs a user gesture, so locked
// mounts wait for the first pointer/key event
// ---------------------------------------------------------------------------
let regrantArmed = false;
function armRegrantOnGesture() {
    if (regrantArmed) {
        return;
    }
    regrantArmed = true;
    const onGesture = () => {
        window.removeEventListener("pointerdown", onGesture);
        window.removeEventListener("keydown", onGesture);
        regrantArmed = false;
        void regrantLockedMounts();
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
}
async function regrantLockedMounts() {
    const locked = core
        .listMounts()
        .filter((mount) => mount.state === "locked" && mount.handle !== null);
    let denied = 0;
    for (const mount of locked) {
        try {
            const handle = mount.handle;
            const state = typeof handle.requestPermission === "function"
                ? await handle.requestPermission({ mode: "readwrite" })
                : "granted";
            if (state === "granted") {
                core.unlockMount(mount.mountPath);
                console.info(`[vortex-web] re-granted access to ${mount.label}`);
                // a re-granted plugins mount may have a pending plugins.txt write
                if (mount.mountPath.startsWith("/plugins/")) {
                    void flushPlugins(mount.mountPath.slice("/plugins/".length));
                }
            }
            else {
                denied++;
            }
        }
        catch (err) {
            // transient activation consumed/expired — try again on the next gesture
            denied++;
            console.warn(`[vortex-web] permission re-grant failed for ${mount.label}`, err);
        }
    }
    if (denied > 0) {
        armRegrantOnGesture();
    }
}
// ---------------------------------------------------------------------------
// plugins.txt (Bethesda) folder grant — a second File System Access mount on
// AppData/Local/<game>, where plugins.txt lives (separate from the game
// install). Registered at /plugins/<gameId>.
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pluginTxt = require("../shims/plugin-txt.cjs");
function flushPlugins(gameId) {
    return pluginTxt
        .flush(gameId)
        .catch((err) => console.warn("[vortex-web] plugins.txt flush failed", err));
}
/**
 * Prompts (via the directory picker) for the AppData/Local/<game> folder that
 * holds plugins.txt, registers it as the /plugins/<gameId> mount, persists it,
 * and writes any pending plugins.txt. Must be called from a user gesture.
 */
export async function requestPluginsFolder(gameId) {
    if (typeof window.showDirectoryPicker !== "function") {
        return false;
    }
    let handle;
    try {
        handle = await window.showDirectoryPicker({ mode: "readwrite" });
    }
    catch (err) {
        if (err?.name !== "AbortError") {
            console.warn("[vortex-web] plugins folder picker failed", err);
        }
        return false;
    }
    const mountPath = `/plugins/${gameId}`;
    const meta = {
        label: `plugins:${gameId}`,
        rootStat: { mtimeMs: Date.now() },
    };
    core.registerMount(mountPath, handle, meta);
    try {
        await idbPut({ mountPath, handle, createdAt: Date.now(), ...meta });
    }
    catch (err) {
        console.warn("[vortex-web] failed to persist plugins mount", err);
    }
    await flushPlugins(gameId);
    return true;
}
function pluginsGranted(gameId) {
    const mountPath = `/plugins/${gameId}`;
    return core
        .listMounts()
        .some((m) => m.mountPath === mountPath && m.state === "live");
}
// A small banner with a button, injected from the web layer (no renderer
// changes). The button click is the user gesture the directory picker needs.
// Shown when a Bethesda game is managed but its plugins folder isn't granted.
function showPluginGrantBanner(gameId) {
    if (typeof document === "undefined" || pluginsGranted(gameId)) {
        return;
    }
    const id = `vortex-web-plugin-grant-${gameId}`;
    if (document.getElementById(id) !== null) {
        return; // already showing
    }
    const folder = pluginTxt.appDataName(gameId) ?? gameId;
    const banner = document.createElement("div");
    banner.id = id;
    banner.style.cssText =
        "position:fixed;bottom:16px;right:16px;z-index:99999;max-width:360px;" +
            "background:#2b2b2b;color:#fff;border:1px solid #555;border-radius:8px;" +
            "padding:12px 14px;font-size:13px;line-height:1.4;" +
            "box-shadow:0 4px 16px rgba(0,0,0,.4)";
    const msg = document.createElement("div");
    msg.style.marginBottom = "10px";
    msg.textContent =
        `To activate mods for this game, Vortex needs access to your ` +
            `"${folder}" folder under AppData/Local — that's where plugins.txt lives.`;
    const grant = document.createElement("button");
    grant.textContent = "Grant plugins folder access";
    grant.style.cssText =
        "background:#d9822b;color:#fff;border:none;border-radius:5px;" +
            "padding:6px 12px;cursor:pointer;font-size:13px;margin-right:8px";
    grant.onclick = () => {
        void requestPluginsFolder(gameId).then((ok) => {
            if (ok) {
                banner.remove();
            }
        });
    };
    const dismiss = document.createElement("button");
    dismiss.textContent = "Later";
    dismiss.style.cssText =
        "background:transparent;color:#bbb;border:none;cursor:pointer;font-size:13px";
    dismiss.onclick = () => banner.remove();
    banner.append(msg, grant, dismiss);
    document.body.appendChild(banner);
}
// plugin-txt asks (at manage time) for the folder grant via this hook
globalThis.__vortexWebPromptPluginsFolder = (gameId) => showPluginGrantBanner(gameId);
// ---------------------------------------------------------------------------
// public api
// ---------------------------------------------------------------------------
/**
 * Preloads small persisted caches from OPFS into the fs sync snapshot, so the
 * renderer's synchronous reads of them succeed after a reload. Currently the
 * game-image cache, which lets the sidebar game icons resolve immediately
 * instead of falling back to a letter avatar.
 */
export async function prewarmCaches() {
    await core.prewarmSync("/vortex/userData/icon_cache");
}
/**
 * Restores persisted mounts. Called once at boot, before the renderer
 * evaluates, so startup game-discovery validation sees the mounts.
 */
export async function restoreMounts() {
    // automated tests inject fake handles and skip IndexedDB
    const testMounts = window.__vortexWebTestMounts;
    if (testMounts !== undefined) {
        for (const [mountPath, handle] of Object.entries(testMounts)) {
            core.registerMount(mountPath, handle);
        }
        return;
    }
    let records;
    try {
        records = await idbGetAll();
    }
    catch (err) {
        console.warn("[vortex-web] failed to read persisted mounts", err);
        return;
    }
    let anyLocked = false;
    for (const record of records) {
        try {
            const state = await queryPermission(record.handle);
            if (state === "granted") {
                core.registerMount(record.mountPath, record.handle, {
                    label: record.label,
                    rootStat: record.rootStat,
                });
            }
            else {
                anyLocked = true;
                core.registerLockedMount(record.mountPath, {
                    handle: record.handle,
                    label: record.label,
                    rootStat: record.rootStat,
                });
            }
        }
        catch (err) {
            console.warn(`[vortex-web] failed to restore mount ${record.mountPath}`, err);
        }
    }
    if (anyLocked) {
        armRegrantOnGesture();
    }
}
/**
 * Shows the browser directory picker and registers the chosen directory as
 * a mount. Returns the virtual mount path, or null if unavailable/cancelled.
 */
export async function pickDirectoryMount() {
    if (typeof window.showDirectoryPicker !== "function") {
        console.warn("[vortex-web] the File System Access API is not available in this " +
            "browser (Chrome/Edge only) — directory selection is fused off");
        return null;
    }
    let handle;
    try {
        handle = await window.showDirectoryPicker({ mode: "readwrite" });
    }
    catch (err) {
        if (err?.name === "AbortError") {
            return null; // user cancelled
        }
        console.warn("[vortex-web] directory picker failed", err);
        return null;
    }
    // re-picking the same folder reuses its mount so persisted discovery
    // state (which stores the mount path) stays stable
    for (const mount of core.listMounts()) {
        if (mount.handle !== null &&
            typeof handle.isSameEntry === "function" &&
            (await handle.isSameEntry(mount.handle))) {
            core.unlockMount(mount.mountPath, handle);
            return mount.mountPath;
        }
    }
    const label = handle.name ?? "folder";
    const mountPath = uniqueMountPath(label);
    const rootStat = { mtimeMs: Date.now() };
    core.registerMount(mountPath, handle, { label, rootStat });
    try {
        await idbPut({
            mountPath,
            handle,
            label,
            createdAt: Date.now(),
            rootStat,
        });
    }
    catch (err) {
        // non-fatal: the mount works for this session, it just won't survive a reload
        console.warn("[vortex-web] failed to persist mount handle", err);
    }
    return mountPath;
}
function fileSlug(name) {
    // keep the extension so the installer's format detection works
    return name.replace(/[^a-zA-Z0-9._-]+/g, "_") || "file";
}
/**
 * Shows the browser file picker and registers the chosen file at a virtual
 * path the renderer can read by path (used for "Install From File"). Returns
 * the virtual path, or null if unavailable/cancelled. The file is read-only
 * and lives only for this session — long enough to be extracted into staging.
 */
export async function pickFileMount() {
    // test hook: a fake file handle injected without a native picker
    const testFile = window.__vortexWebTestFile;
    if (testFile !== undefined) {
        const name = fileSlug(testFile.handle.name ?? "file.zip");
        return core.registerFileMount(testFile.path, testFile.handle, name);
    }
    if (typeof window.showOpenFilePicker !== "function") {
        console.warn("[vortex-web] the File System Access API is not available in this " +
            "browser (Chrome/Edge only) — file selection is fused off");
        return null;
    }
    let handle;
    try {
        [handle] = await window.showOpenFilePicker({ multiple: false });
    }
    catch (err) {
        if (err?.name === "AbortError") {
            return null; // user cancelled
        }
        console.warn("[vortex-web] file picker failed", err);
        return null;
    }
    const name = handle.name ?? "file";
    // virtual path outside the userData (OPFS) and game-mount namespaces
    const filePath = `/fsa-file/${fileSlug(name)}`;
    core.registerFileMount(filePath, handle, name);
    return filePath;
}
